import { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useEffect, useRef, useState } from 'react'
import { LoadingLivestream } from '../components/LoadingLivestream'
import type { VideoWallTile } from './types'
import { useVideoTiles } from './useVideoTiles'
import {
  FRONT_CAMERA_PREVIEW_HIDDEN_NUMBERS,
  FRONT_CAMERA_PREVIEW_NUMBER,
  getFocusedTiles,
} from './utils'
import { NoVideoTile, PreviewTile, VideoTile } from './VideoTile'

const TILE_TIMEOUT_MS = 10_000

const FOCUSED_TILE_MAX_HEIGHT = 400
const FOCUSED_TILE_MAX_WIDTH = 640
const FOCUSED_TILE_COMPACT_QUERY = '@container (max-width: 1000px)'
const PREVIEW_TILE_WIDTH = 200

type VideoWallCockpitViewProps = {
  vehicleId: string
}

export const VideoWallCockpitView = ({
  vehicleId,
}: VideoWallCockpitViewProps) => {
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const previewRefs = useRef<Array<HTMLCanvasElement | null>>([])
  const tiles = useVideoTiles()
  const [focusedTile, setFocusedTile] = useState(tiles.at(0))
  const [timedOut, setTimedOut] = useState(false)

  // Chime may need a moment to attach remote video tiles; only show retry after a short grace period.
  useEffect(() => {
    if (tiles.length > 0 || timedOut) return
    const id = setTimeout(() => setTimedOut(true), TILE_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [tiles.length, timedOut])
  const { trackMetric } = useMetricTracking()

  const cameraSelectionStartTime = useRef<number | null>(null)
  const lastSelectedCamera = useRef<string | null>(null)

  const handleCameraSelection = async (
    tile: VideoWallTile,
    selectionType: 'manual' | 'automatic' = 'manual',
  ) => {
    if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
      const selectionDuration = Date.now() - cameraSelectionStartTime.current
      await trackMetric({
        metricName: 'CameraSelectionTime',
        value: selectionDuration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'cameraName', value: lastSelectedCamera.current },
          { name: 'vehicleId', value: vehicleId },
          { name: 'tileId', value: String(focusedTile?.id ?? -1) },
          { name: 'selectionType', value: selectionType },
        ],
        namespace: 'FleetControl/ad-operator-video',
      })
    }

    setFocusedTile(tile)
    lastSelectedCamera.current = tile.name
    cameraSelectionStartTime.current = Date.now()
  }

  // Previews use canvases because each Chime tile is already bound to a single video element.
  useEffect(() => {
    const videos = videoRefs.current
    const previews = previewRefs.current
    if (!videos || !previews) return

    const cleanups: Array<() => void> = []

    for (const tile of tiles) {
      const video = videos[tile.id]
      const preview = previews[tile.id]
      if (!video || !preview) continue

      const context = preview.getContext('2d')
      if (!context) continue

      let animationFrameId: number | undefined

      const drawPreview = () => {
        if (video.ended) return
        const width = video.videoWidth
        const height = video.videoHeight
        if (width === 0 || height === 0) return

        if (preview.width !== width) preview.width = width
        if (preview.height !== height) preview.height = height

        context.drawImage(video, 0, 0, width, height)
        animationFrameId = requestAnimationFrame(drawPreview)
      }

      const startDrawing = () => {
        if (animationFrameId !== undefined) return
        drawPreview()
      }

      if (video.videoWidth > 0 && video.videoHeight > 0) {
        startDrawing()
      } else {
        video.addEventListener('loadedmetadata', startDrawing)
        video.addEventListener('canplay', startDrawing)
      }

      cleanups.push(() => {
        video.removeEventListener('loadedmetadata', startDrawing)
        video.removeEventListener('canplay', startDrawing)
        if (animationFrameId !== undefined) {
          cancelAnimationFrame(animationFrameId)
        }
      })
    }

    return () => {
      cleanups.forEach(cleanup => {
        cleanup()
      })
    }
  }, [tiles])

  // Streams can appear or disappear while reconnecting, so keep the focused tile available and track automatic switches.
  useEffect(() => {
    const trackCameraChange = (
      newTile: { id: number; name: string } | undefined,
    ) => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'selectionType', value: 'automatic' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }

      if (newTile) {
        lastSelectedCamera.current = newTile.name
        cameraSelectionStartTime.current = Date.now()
      } else {
        lastSelectedCamera.current = null
        cameraSelectionStartTime.current = null
      }
    }

    if (focusedTile && tiles.some(tile => tile.id === focusedTile.id)) {
      return
    }

    // focused tile set but not available anymore -> update or remove
    if (focusedTile && !tiles.some(tile => tile.id === focusedTile.id)) {
      const newTile = tiles.length > 0 ? tiles.at(0) : undefined
      trackCameraChange(newTile)
      setFocusedTile(newTile)
      return
    }

    // focused tile NOT set and tiles available -> set
    if (focusedTile === undefined && tiles.length > 0) {
      const newTile = tiles.at(0)
      trackCameraChange(newTile)
      setFocusedTile(newTile)
    }
  }, [tiles, focusedTile, trackMetric, vehicleId])

  // Flush the final camera view duration on unmount so the last selection is included in tracking.
  useEffect(() => {
    return () => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'sessionEnd', value: '1' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }
    }
  }, [trackMetric, vehicleId])

  if (!focusedTile) {
    return timedOut ? (
      <NoVideoTile onRetry={() => setTimedOut(false)} />
    ) : (
      <LoadingLivestream />
    )
  }

  const focusedTiles = getFocusedTiles(focusedTile, tiles)
  const compactFocusedTiles =
    focusedTile.number === FRONT_CAMERA_PREVIEW_NUMBER
      ? [focusedTile]
      : focusedTiles
  const focusedTileColumnCount = Math.max(1, focusedTiles.length)

  return (
    <div
      css={theme => ({
        containerType: 'inline-size',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space1,
      })}
    >
      {/* Focused video */}
      <div
        css={theme => ({
          display: 'grid',
          alignItems: 'center',
          justifyItems: 'center',
          justifyContent: 'center',
          gridTemplateColumns: `repeat(${focusedTileColumnCount}, minmax(0, ${FOCUSED_TILE_MAX_WIDTH}px))`,
          gap: theme.space.Space1,
          padding: theme.space.Space1,
          backgroundColor: theme.semantic.ColorSurfaceReduced,
          height: FOCUSED_TILE_MAX_HEIGHT,
          [FOCUSED_TILE_COMPACT_QUERY]: {
            gridTemplateColumns: '1fr',
            padding: 0,
            height: 'auto',
            minHeight: 300,
            borderRadius: 0,
          },
          position: 'relative',
          borderRadius: theme.space.Space2,
          overflow: 'hidden',
          '& > *': {
            gridRow: 1,
          },
        })}
      >
        {tiles.map(tile => {
          const focusedTileIndex = focusedTiles.findIndex(
            focusedTile => focusedTile.id === tile.id,
          )
          const isFocusedInMainView = focusedTileIndex !== -1
          const compactFocusedTileIndex = compactFocusedTiles.findIndex(
            focusedTile => focusedTile.id === tile.id,
          )
          const isFocusedInCompactView = compactFocusedTileIndex !== -1

          return (
            <div
              key={tile.id}
              css={theme => ({
                visibility: isFocusedInMainView ? 'visible' : 'hidden',
                gridColumn: isFocusedInMainView ? focusedTileIndex + 1 : 1,
                position: isFocusedInMainView ? 'relative' : 'absolute',
                width: isFocusedInMainView ? '100%' : 1,
                height: isFocusedInMainView ? undefined : 1,
                maxHeight: FOCUSED_TILE_MAX_HEIGHT,
                aspectRatio: '16 / 10',
                borderRadius: theme.space.Space2,
                overflow: 'hidden',
                pointerEvents: isFocusedInMainView ? undefined : 'none',
                [FOCUSED_TILE_COMPACT_QUERY]: {
                  visibility: isFocusedInCompactView ? 'visible' : 'hidden',
                  gridColumn: isFocusedInCompactView
                    ? compactFocusedTileIndex + 1
                    : 1,
                  position: isFocusedInCompactView ? 'relative' : 'absolute',
                  width: isFocusedInCompactView ? '100%' : 1,
                  height: isFocusedInCompactView ? undefined : 1,
                  borderRadius: 0,
                  pointerEvents: isFocusedInCompactView ? undefined : 'none',
                },
                '& video': {
                  borderRadius: theme.space.Space2,
                  height: 'auto',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  width: 'auto',
                  [FOCUSED_TILE_COMPACT_QUERY]: {
                    borderRadius: 0,
                  },
                },
                '& > div': {
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              })}
            >
              <VideoTile
                ref={_ref => {
                  videoRefs.current[tile.id] = _ref
                }}
                tileId={tile.id}
                name={tile.name}
              />
            </div>
          )
        })}
      </div>

      {/* Previews */}
      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          gap: theme.space.Space1,
        })}
      >
        {tiles.map(tile => {
          const isFocused = tile.id === focusedTile.id
          const isHiddenInWideView = FRONT_CAMERA_PREVIEW_HIDDEN_NUMBERS.has(
            tile.number,
          )
          return (
            <div
              key={`preview-${tile.id}`}
              css={theme => ({
                display: isHiddenInWideView ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.semantic.ColorSurfaceReduced,
                overflow: 'hidden',
                borderRadius: theme.space.Space2,
                width: PREVIEW_TILE_WIDTH,
                flex: '0 0 auto',
                aspectRatio: '16 / 10',
                cursor: 'pointer',
                boxShadow: isFocused
                  ? `inset 0 0 0 4px ${theme.semantic.ColorContentDefault}`
                  : 'none',
                [FOCUSED_TILE_COMPACT_QUERY]: {
                  display: 'flex',
                  width: `calc((100% - ${theme.space.Space1} - ${theme.space.Space1} - ${theme.space.Space1} - ${theme.space.Space1}) / 5)`,
                },
              })}
              onClick={() => handleCameraSelection(tile, 'manual')}
            >
              <PreviewTile
                ref={_ref => {
                  previewRefs.current[tile.id] = _ref
                }}
                name={tile.name}
                isFocused={isFocused}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
